import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

type Tone = "emerald" | "amber" | "rose" | "blue" | "violet" | "slate";

const toneClasses: Record<Tone, { icon: string; bar: string; text: string }> = {
  amber: {
    icon: "tw:bg-amber-50 tw:text-amber-700 tw:ring-amber-100",
    bar: "tw:bg-amber-500",
    text: "tw:text-amber-700"
  },
  blue: {
    icon: "tw:bg-blue-50 tw:text-blue-700 tw:ring-blue-100",
    bar: "tw:bg-blue-500",
    text: "tw:text-blue-700"
  },
  emerald: {
    icon: "tw:bg-emerald-50 tw:text-emerald-700 tw:ring-emerald-100",
    bar: "tw:bg-emerald-500",
    text: "tw:text-emerald-700"
  },
  rose: {
    icon: "tw:bg-rose-50 tw:text-rose-700 tw:ring-rose-100",
    bar: "tw:bg-rose-500",
    text: "tw:text-rose-700"
  },
  slate: {
    icon: "tw:bg-slate-100 tw:text-slate-700 tw:ring-slate-200",
    bar: "tw:bg-slate-500",
    text: "tw:text-slate-700"
  },
  violet: {
    icon: "tw:bg-violet-50 tw:text-violet-700 tw:ring-violet-100",
    bar: "tw:bg-violet-500",
    text: "tw:text-violet-700"
  }
};

export function OpsPageShell({ children }: { children: ReactNode }) {
  return <div className="tw:space-y-4">{children}</div>;
}

export function OpsHeader({
  eyebrow,
  title,
  description,
  actions
}: {
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="tw:rounded-lg tw:border tw:border-slate-200/80 tw:bg-white/90 tw:p-4 tw:shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
      <div className="tw:flex tw:flex-col tw:gap-4 tw:xl:flex-row tw:xl:items-center tw:xl:justify-between">
        <div className="tw:min-w-0">
          <p className="tw:m-0 tw:text-[0.68rem] tw:font-bold tw:uppercase tw:tracking-[0.18em] tw:text-emerald-700">{eyebrow}</p>
          <h1 className="tw:m-0 tw:mt-1 tw:text-3xl tw:font-bold tw:leading-tight tw:text-slate-950">{title}</h1>
          {description ? <p className="tw:m-0 tw:mt-2 tw:max-w-3xl tw:text-sm tw:text-slate-600">{description}</p> : null}
        </div>
        {actions ? <div className="tw:flex tw:flex-wrap tw:justify-start tw:gap-2 tw:xl:justify-end">{actions}</div> : null}
      </div>
    </section>
  );
}

export function OpsMetricGrid({ children }: { children: ReactNode }) {
  return <div className="tw:grid tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-4">{children}</div>;
}

export function OpsMetricCard({
  label,
  value,
  detail,
  icon: Icon,
  tone = "slate"
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  icon: LucideIcon;
  tone?: Tone;
}) {
  return (
    <div className="tw:rounded-lg tw:border tw:border-slate-200/80 tw:bg-white/90 tw:p-4 tw:shadow-[0_10px_24px_rgba(15,23,42,0.06)]">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div className="tw:min-w-0">
          <p className="tw:m-0 tw:text-sm tw:font-semibold tw:text-slate-500">{label}</p>
          <p className="tw:m-0 tw:mt-2 tw:text-2xl tw:font-bold tw:leading-tight tw:text-slate-950">{value}</p>
        </div>
        <span className={`tw:grid tw:size-10 tw:shrink-0 tw:place-items-center tw:rounded-lg tw:ring-1 ${toneClasses[tone].icon}`}>
          <Icon className="tw:size-5" aria-hidden="true" />
        </span>
      </div>
      <div className={`tw:mt-4 tw:h-1 tw:w-12 tw:rounded-full ${toneClasses[tone].bar}`} />
      {detail ? <p className="tw:m-0 tw:mt-3 tw:text-sm tw:text-slate-500">{detail}</p> : null}
    </div>
  );
}

export function OpsPanel({
  title,
  eyebrow,
  actions,
  children,
  className = ""
}: {
  title?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`tw:rounded-lg tw:border tw:border-slate-200/80 tw:bg-white/85 tw:p-4 tw:shadow-[0_12px_30px_rgba(15,23,42,0.08)] ${className}`}>
      {title || eyebrow || actions ? (
        <div className="tw:mb-4 tw:flex tw:items-start tw:justify-between tw:gap-3">
          <div className="tw:min-w-0">
            {eyebrow ? <p className="tw:m-0 tw:text-[0.68rem] tw:font-bold tw:uppercase tw:tracking-[0.16em] tw:text-slate-500">{eyebrow}</p> : null}
            {title ? <h2 className="tw:m-0 tw:mt-1 tw:text-lg tw:font-bold tw:leading-tight tw:text-slate-950">{title}</h2> : null}
          </div>
          {actions ? <div className="tw:flex tw:shrink-0 tw:flex-wrap tw:items-center tw:justify-end tw:gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}
